import {QueryClient} from '@tanstack/react-query';
import {httpBatchLink, httpLink, splitLink} from '@trpc/client';
import {createTRPCProxyClient, createTRPCReact} from '@trpc/react-query';
import type {CreateTRPCReact} from '@trpc/react-query';
import {inferRouterInputs, inferRouterOutputs} from '@trpc/server';
import superjson from 'superjson';
import type {AppRouter} from '@api/routers';
import {STALE_TIME_10_SEC} from '@common/constants/time';
import {DEV_API_URL} from '@common/constants/network';
import {toast} from '@common-web/hooks/use-toast';
import {reportError} from '@common-web/lib/errorReporting';
import {getViewAsBrokerId} from '@common-web/stores/viewAsStore';

/**
 * When a client admin is "viewing as" a broker, echo the broker id on every
 * request so the API can swap the request context to that broker. Read lazily
 * per-request from the persisted store.
 */
function viewAsHeaders(): Record<string, string> {
  const agentId = getViewAsBrokerId();
  return agentId ? {'x-view-as-broker-id': agentId} : {};
}

// Flatten Zod issues, including nested unionErrors
function flattenZodIssues(issues: any[] | undefined): any[] {
  if (!Array.isArray(issues)) return [];
  const flat: any[] = [];
  for (const issue of issues) {
    if (issue?.code === 'invalid_union' && Array.isArray(issue.unionErrors)) {
      for (const unionErr of issue.unionErrors) {
        if (Array.isArray(unionErr?.issues)) {
          flat.push(...flattenZodIssues(unionErr.issues));
        }
      }
    } else if (Array.isArray(issue?.issues)) {
      flat.push(...flattenZodIssues(issue.issues));
    } else {
      flat.push(issue);
    }
  }
  return flat;
}

function summarizeMessages(messages: string[], limit: number = 3): string {
  const unique = Array.from(new Set(messages.filter(Boolean)));
  if (unique.length === 0) return 'Invalid input';
  return unique.slice(0, limit).join('; ') + (unique.length > limit ? '...' : '');
}

export function formatTRPCError(error: unknown): string {
  try {
    if (typeof error === 'object' && error !== null) {
      const err = error as any;

      const zodContainer =
        err?.data?.zodError ??
        err?.zodError ??
        err?.cause?.zodError ??
        (Array.isArray(err?.data?.issues) ? {issues: err.data.issues} : undefined) ??
        (Array.isArray(err?.issues) ? {issues: err.issues} : undefined);

      if (zodContainer) {
        const messages: string[] = [];

        if (zodContainer.fieldErrors && typeof zodContainer.fieldErrors === 'object') {
          for (const [field, errs] of Object.entries(
            zodContainer.fieldErrors as Record<string, string[]>,
          )) {
            if (Array.isArray(errs) && errs.length) {
              messages.push(`${field}: ${errs.join(', ')}`);
            }
          }
        }

        if (Array.isArray(zodContainer.issues)) {
          const flatIssues = flattenZodIssues(zodContainer.issues);
          for (const issue of flatIssues) {
            const rawPath = Array.isArray(issue.path) ? [...issue.path] : [];
            if (rawPath[0] === 'data') rawPath.shift();
            const label = rawPath.length > 0 ? rawPath.join('.') : issue.code || 'error';
            const msg =
              typeof issue.message === 'string' && issue.message.trim()
                ? issue.message
                : 'Invalid input';
            messages.push(`${label}: ${msg}`);
          }
        }

        if (messages.length > 0) {
          return summarizeMessages(messages, 3);
        }
      }

      // Fallback to message when no structured Zod details are available
      if (typeof err.message === 'string' && err.message.trim()) {
        // Attempt to parse JSON array payloads sometimes embedded in message
        try {
          const parsed = JSON.parse(err.message);
          if (Array.isArray(parsed)) {
            const nestedIssues = parsed.flatMap((p: any) =>
              Array.isArray(p?.issues) ? p.issues : p?.data?.zodError?.issues || [],
            );
            const flat = flattenZodIssues(nestedIssues);
            const msgs = flat.map((i: any) => {
              const rawPath = Array.isArray(i.path) ? [...i.path] : [];
              if (rawPath[0] === 'data') rawPath.shift();
              const label = rawPath.length > 0 ? rawPath.join('.') : i.code || 'error';
              return `${label}: ${i.message || 'Invalid input'}`;
            });
            if (msgs.length > 0) return summarizeMessages(msgs, 3);
          }
        } catch {}
        return err.message;
      }
    }
  } catch {}
  return 'An unexpected error occurred. Please try again.';
}

export function createMutationErrorHandler(title: string = 'Error') {
  return (error: unknown) => {
    reportError(error, {title});
  };
}

export const trpc: CreateTRPCReact<AppRouter, unknown> = createTRPCReact<AppRouter>();

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_10_SEC,
    },
  },
});

export const api = createTRPCProxyClient<AppRouter>({
  links: [
    splitLink({
      condition(op) {
        // check for context property `skipBatch`
        return op.context.skipBatch === true;
      },
      // when condition is true, use normal request
      true: httpLink({
        transformer: superjson,
        url: `${getBaseUrl()}/api`,
        headers: viewAsHeaders,
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: 'include',
          });
        },
      }),
      // when condition is false, use batching
      false: httpBatchLink({
        transformer: superjson,
        url: `${getBaseUrl()}/api`,
        headers: viewAsHeaders,
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: 'include',
          });
        },
      }),
    }),
  ],
});

export function getBaseUrl() {
  if (import.meta.env.VITE_API_URL && window.location.hostname.includes('cbs-infinity.com')) {
    return import.meta.env.VITE_API_URL;
  }
  if (window.location.hostname.includes('localhost')) {
    return DEV_API_URL;
  }
  // Custom domains (e.g., portal.quantumhb.com) use CloudFront API proxy — same-origin
  if (!window.location.hostname.includes('cbs-infinity.com')) {
    return '';
  }
  return `https://api.cbs-infinity.com`;
}

export const trpcClient = trpc.createClient({
  links: [
    splitLink({
      condition(op) {
        // check for context property `skipBatch`
        return op.context.skipBatch === true;
      },
      // when condition is true, use normal request
      true: httpLink({
        transformer: superjson,
        url: `${getBaseUrl()}/api`,
        headers: viewAsHeaders,
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: 'include',
          });
        },
      }),
      // when condition is false, use batching
      false: httpBatchLink({
        transformer: superjson,
        url: `${getBaseUrl()}/api`,
        headers: viewAsHeaders,
        fetch(url, options) {
          return fetch(url, {
            ...options,
            credentials: 'include',
          });
        },
      }),
    }),
  ],
});

export type RouterInput = inferRouterInputs<AppRouter>;
export type RouterOutput = inferRouterOutputs<AppRouter>;
